import { create } from "zustand";
import * as yaml from "js-yaml";
import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import i18next from "i18next";

import { ChatPersonalityType } from "@/components/Chat/ChatPersonality";
import { logger } from "@/telemetry";

export interface UserInteractionConfig {
  maxTurns?: number | null | undefined;
  terminationCondition?: Array<string> | null | undefined;
}

export interface Agent {
  type: string;
  parameters: Record<string, any>;
  triggers: Trigger[];
  userInteraction?: UserInteractionConfig | null | undefined;
  team?: string | null | undefined;
  description?: string | null | undefined;
}

export type TeamStrategy = "selector" | "mixture";

// YAML snake_case versions of interfaces (for parsing YAML files)
interface YamlUserInteractionConfig {
  max_turns?: number | null;
  termination_condition?: Array<string> | null;
}

interface YamlAgent {
  type: string;
  parameters: Record<string, any>;
  triggers: Trigger[];
  tools: string[];
  user_interaction?: YamlUserInteractionConfig | null;
  team?: string | null;
  description?: string | null;
}

export interface Team {
  members: string[];
  strategy?: TeamStrategy | undefined;
  maxCycles?: number | undefined;
  goal: string;
  useLlmJudgment?: boolean | undefined;
  selectorPrompt?: string | null | undefined;
  aggregatorPrompt?: string | null | undefined;
  triggers: Trigger[];
  model?: string | undefined;
}

export interface Trigger {
  source: string;
  mapping: Record<string, any>;
}

export interface Workflow {
  name: string;
  id: string;
  tenant: string;
  description: string;
  input: Record<string, any> | null | undefined;
  teams?: Record<string, Team> | null | undefined;
  agents: Record<string, Agent>;
}

export interface WorkflowStore {
  workflow: Workflow | null;
  isLoadingWorkflow: boolean;
  setWorkflow: (workflow: Workflow | null) => void;
  setWorkflowFromYaml: (yamlString: string, platformClient: WebuildAiPlatform) => Promise<void>;
  setWorkflowName: (name: string) => void;
  setWorkflowDescription: (description: string) => void;
  getWorkflow: () => Workflow | null;
  loadWorkflow: (workflowId: string, adminClient: WeBuildAiAdmin) => Promise<void>;
  saveWorkflow: (adminClient: WeBuildAiAdmin) => Promise<void>;
  setPersonality: (personality: ChatPersonalityType) => void;
  updateAgentTools: (agent_id: string, tool_id: string, isSelected: boolean) => void;
}

// Transform snake_case team fields from YAML to camelCase for SDK compatibility
// YAML uses snake_case (e.g., max_cycles), but SDK expects camelCase (e.g., maxCycles)
interface YamlTeam {
  members: string[];
  strategy?: TeamStrategy;
  max_cycles?: number;
  goal: string;
  use_llm_judgment?: boolean;
  selector_prompt?: string | null;
  aggregator_prompt?: string | null;
  triggers: Trigger[];
  model?: string;
}

function transformTeamFromYaml(yamlTeam: YamlTeam): Team {
  return {
    members: yamlTeam.members,
    strategy: yamlTeam.strategy,
    maxCycles: yamlTeam.max_cycles,
    goal: yamlTeam.goal,
    useLlmJudgment: yamlTeam.use_llm_judgment,
    selectorPrompt: yamlTeam.selector_prompt,
    aggregatorPrompt: yamlTeam.aggregator_prompt,
    triggers: yamlTeam.triggers ?? [],
    model: yamlTeam.model,
  };
}

function transformTeamsFromYaml(
  yamlTeams: Record<string, YamlTeam> | null | undefined,
): Record<string, Team> | null | undefined {
  if (!yamlTeams) return yamlTeams;

  const transformedTeams: Record<string, Team> = {};

  for (const [teamId, team] of Object.entries(yamlTeams)) {
    transformedTeams[teamId] = transformTeamFromYaml(team);
  }

  return transformedTeams;
}

// Transform agent's user_interaction from snake_case to camelCase
function transformUserInteractionFromYaml(
  yamlConfig: YamlUserInteractionConfig | null | undefined,
): UserInteractionConfig | null | undefined {
  if (!yamlConfig) return yamlConfig;

  return {
    maxTurns: yamlConfig.max_turns,
    terminationCondition: yamlConfig.termination_condition,
  };
}

function transformAgentFromYaml(yamlAgent: YamlAgent): Agent {
  // Move tools into parameters to match standardized structure
  const parameters = { ...yamlAgent.parameters };

  if (yamlAgent.tools) {
    parameters.tools = yamlAgent.tools;
  }

  return {
    type: yamlAgent.type,
    parameters,
    triggers: yamlAgent.triggers ?? [],
    userInteraction: transformUserInteractionFromYaml(yamlAgent.user_interaction),
    team: yamlAgent.team,
    description: yamlAgent.description,
  };
}

function transformAgentsFromYaml(yamlAgents: Record<string, YamlAgent>): Record<string, Agent> {
  const transformedAgents: Record<string, Agent> = {};

  for (const [agentId, agent] of Object.entries(yamlAgents)) {
    transformedAgents[agentId] = transformAgentFromYaml(agent);
  }

  return transformedAgents;
}

// Reverse transforms: camelCase model → snake_case for YAML serialization
// Without these, yaml.stringify(workflow) outputs camelCase keys which break
// the snake_case-only transform functions on subsequent edits (lossy round-trip).

function transformUserInteractionToYaml(
  config: UserInteractionConfig | null | undefined,
): YamlUserInteractionConfig | null | undefined {
  if (!config) return config;

  return {
    max_turns: config.maxTurns,
    termination_condition: config.terminationCondition,
  };
}

function transformAgentToYaml(agent: Agent): YamlAgent {
  return {
    type: agent.type,
    parameters: agent.parameters,
    triggers: agent.triggers ?? [],
    tools: agent.parameters?.tools ?? [],
    user_interaction: transformUserInteractionToYaml(agent.userInteraction),
    team: agent.team,
    description: agent.description,
  };
}

function transformTeamToYaml(team: Team): YamlTeam {
  return {
    members: team.members,
    strategy: team.strategy,
    max_cycles: team.maxCycles,
    goal: team.goal,
    use_llm_judgment: team.useLlmJudgment,
    selector_prompt: team.selectorPrompt,
    aggregator_prompt: team.aggregatorPrompt,
    triggers: team.triggers ?? [],
    model: team.model,
  };
}

/**
 * Converts a camelCase Workflow model to a snake_case object suitable for YAML serialization.
 * This ensures the YAML editor always displays snake_case keys matching the backend format,
 * preventing the lossy round-trip where camelCase YAML gets re-parsed by snake_case-only transforms.
 */
export function workflowToYamlObject(workflow: Workflow): Record<string, unknown> {
  const agents: Record<string, YamlAgent> = {};

  for (const [agentId, agent] of Object.entries(workflow.agents)) {
    agents[agentId] = transformAgentToYaml(agent);
  }

  let teams: Record<string, YamlTeam> | null | undefined = workflow.teams ? {} : workflow.teams;

  if (workflow.teams && teams) {
    for (const [teamId, team] of Object.entries(workflow.teams)) {
      teams[teamId] = transformTeamToYaml(team);
    }
  }

  // Some fields (icon, chatManagerPrompt, scheduled) exist at runtime from YAML spread
  // but aren't in the Workflow interface — access via indexing to preserve them.
  const extra = workflow as any;

  return {
    name: workflow.name,
    id: workflow.id,
    description: workflow.description,
    tenant: workflow.tenant,
    icon: extra["icon"] ?? null,
    chat_manager_prompt: extra["chatManagerPrompt"] ?? extra["chat_manager_prompt"] ?? null,
    scheduled: extra["scheduled"] ?? null,
    input: workflow.input,
    teams,
    agents,
  };
}

//TODO: load a baseline workflow
export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  workflow: null,
  isLoadingWorkflow: false,

  updateAgentTools: (agent_id: string, tool_id: string, isSelected: boolean) => {
    const workflow = get().workflow;

    if (!workflow) return;
    const currentTools = workflow.agents[agent_id].parameters?.tools || [];
    const updatedTools = isSelected
      ? [...currentTools, tool_id]
      : currentTools.filter((tool: string) => tool !== tool_id);

    // Update in both formats to ensure consistency with backend
    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            tools: updatedTools,
          },
        },
      },
    };

    set({ workflow: updatedWorkflow });
  },

  setWorkflow: (workflow: Workflow | null) => {
    const currentWorkflow = get().workflow;

    // Only update if the workflow is different
    if (JSON.stringify(workflow) !== JSON.stringify(currentWorkflow)) {
      set({ workflow });
    }
  },

  loadWorkflow: async (workflowId: string, adminClient: WeBuildAiAdmin) => {
    logger.info("🔄 loadWorkflow started", { workflowId, hasAdminClient: String(!!adminClient) });
    const currentWorkflow = get().workflow;
    const isCurrentlyLoading = get().isLoadingWorkflow;

    logger.info("📋 currentWorkflow state", {
      hasCurrentWorkflow: String(!!currentWorkflow),
      currentWorkflowId: String(currentWorkflow?.id),
      currentWorkflowName: String(currentWorkflow?.name),
      requestedWorkflowId: workflowId,
      isCurrentlyLoading: String(isCurrentlyLoading),
    });

    // Check if the workflow is already loaded to prevent looping
    if (currentWorkflow && currentWorkflow.id === workflowId) {
      logger.info("✅ Workflow already loaded - skipping fetch", {
        workflowId: currentWorkflow.id,
        name: String(currentWorkflow.name),
      });

      return;
    }

    // Prevent concurrent loading of the same workflow
    if (isCurrentlyLoading) {
      logger.info("⏳ Workflow loading already in progress - waiting...");
      // Wait for current loading to complete by polling
      while (get().isLoadingWorkflow) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      logger.info("✅ Workflow loading completed by another call");

      return;
    }

    // Clear stale workflow immediately so UI doesn't flash old data
    set({ workflow: null, isLoadingWorkflow: true });

    try {
      logger.info("🌐 Fetching workflow from API", { workflowId });
      const workflow = await adminClient?.workflows.getWorkflow({
        workflowId: workflowId,
      });

      logger.info("📥 API response received", {
        hasWorkflow: String(!!workflow),
        workflowId: String(workflow?.id),
        workflowName: String(workflow?.name),
      });

      if (workflow) {
        logger.info("💾 Setting workflow in store", { workflowId: String(workflow.id) });
        set({ workflow: workflow as Workflow, isLoadingWorkflow: false });
        logger.info("✅ Workflow loaded successfully in store");
      } else {
        logger.error("❌ No workflow returned from API");
        set({ workflow: null, isLoadingWorkflow: false });
      }
    } catch (error) {
      logger.error("❌ Failed to load workflow", error);
      set({ workflow: null, isLoadingWorkflow: false });
      throw error;
    }
  },

  saveWorkflow: async (adminClient: WeBuildAiAdmin) => {
    logger.info("saveWorkflow", { workflowId: String(get().workflow?.id) });
    const currentWorkflow = get().workflow;

    if (currentWorkflow) {
      try {
        await adminClient?.workflows.updateWorkflow({
          workflowId: currentWorkflow.id,
          workflowInput: currentWorkflow,
        });
        logger.info("Workflow saved successfully");
      } catch (error) {
        logger.error("Failed to save workflow", error);
        throw error;
      }
    }
  },

  setWorkflowName: (name: string) => {
    logger.info("setWorkflowName", { name });
    const currentWorkflow = get().workflow;

    if (currentWorkflow) {
      currentWorkflow.name = name;
      set({ workflow: { ...currentWorkflow } });
    }
  },

  setWorkflowDescription: (description: string) => {
    logger.info("setWorkflowDescription", { description });
    const currentWorkflow = get().workflow;

    if (currentWorkflow) {
      currentWorkflow.description = description;
      set({ workflow: { ...currentWorkflow } });
    }
  },

  setPersonality: (personality: ChatPersonalityType) => {
    logger.info("setPersonality", { personality });
    //TODO check if any agent.params in workflow has "personality", then set it
  },

  setWorkflowFromYaml: async (yamlString: string, platformClient: WebuildAiPlatform) => {
    logger.info("setWorkflowFromYaml");

    let parsedWorkflow:
      | (Workflow & { teams?: Record<string, YamlTeam>; agents: Record<string, YamlAgent> })
      | undefined;

    try {
      parsedWorkflow = yaml.load(yamlString) as Workflow & {
        teams?: Record<string, YamlTeam>;
        agents: Record<string, YamlAgent>;
      };
    } catch (yamlError: any) {
      throw new Error(
        yamlError.reason ||
          i18next.t("workflowEditor.syntaxError", {
            defaultValue: "The configuration contains a syntax error",
          }),
      );
    }

    if (!parsedWorkflow || typeof parsedWorkflow !== "object") {
      throw new Error(
        i18next.t("workflowEditor.emptyConfig", {
          defaultValue: "The configuration appears to be empty or incomplete",
        }),
      );
    }

    if (!parsedWorkflow.agents || typeof parsedWorkflow.agents !== "object") {
      throw new Error(
        i18next.t("workflowEditor.missingAgents", {
          defaultValue: "The workflow configuration is missing its agents definition",
        }),
      );
    }

    const currentWorkflow = get().workflow;

    // Transform from snake_case (YAML) to camelCase (SDK)
    // This ensures fields like max_cycles -> maxCycles, user_interaction -> userInteraction
    const transformedWorkflow: Workflow = {
      ...parsedWorkflow,
      teams: transformTeamsFromYaml(parsedWorkflow.teams),
      agents: transformAgentsFromYaml(parsedWorkflow.agents),
    };

    // Only update if the parsed workflow is different
    if (JSON.stringify(transformedWorkflow) === JSON.stringify(currentWorkflow)) {
      return;
    }
    //Test the workflow spec
    await platformClient.workflows.validate({
      workflow: transformedWorkflow,
    });
    set({ workflow: { ...transformedWorkflow } });
  },

  getWorkflow: () => get().workflow,
}));
